"use client";

import * as React from "react";
import {
  Line,
  LineChart as RechartsLineChart,
  CartesianGrid,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  cn,
} from "visuais";

// Tipos específicos para line chart
export type LineDataItem = {
  [key: string]: number | string | Date;
};

export interface LineChartProps {
  data: LineDataItem[];
  className?: string;
  height?: number | string;
  width?: number | string;
  xKey: string;
  yKeys: string[];
  showTooltip?: boolean;
  showGrid?: boolean;
  showLegend?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  loading?: boolean;
  error?: string | null;

  // Variações
  variant?:
    | "default"
    | "linear"
    | "step"
    | "multiple"
    | "dots"
    | "custom-dots"
    | "colored-dots"
    | "labels"
    | "custom-labels";

  // Para linhas
  strokeWidth?: number;
  smooth?: boolean;

  // Para dots
  showDots?: boolean;
  dotSize?: number;
  dotColor?: string;
  customDot?: (props: any) => React.ReactElement;

  // Para labels
  showLabels?: boolean;
  labelPosition?: "top" | "bottom" | "left" | "right";
  customLabel?: (props: any) => React.ReactElement;

  // Formatação de eixos
  xAxisFormatter?: (value: any) => string;
  yAxisFormatter?: (value: any) => string;

  // Configuração
  config?: Record<
    string,
    { label?: string; color?: string; strokeWidth?: number }
  >;
}

export function LineChart({
  data,
  className,
  height = 250,
  width = "100%",
  xKey,
  yKeys,
  showTooltip = true,
  showGrid = true,
  showLegend = false,
  showXAxis = true,
  showYAxis = true,
  loading = false,
  error = null,
  variant = "default",
  strokeWidth = 2,
  smooth = true,
  showDots = false,
  dotSize = 4,
  dotColor,
  customDot,
  showLabels = false,
  labelPosition = "top",
  customLabel,
  xAxisFormatter,
  yAxisFormatter,
  config = {},
}: LineChartProps) {
  // Configuração do chart config para compatibilidade com shadcn
  const chartConfig = React.useMemo(() => {
    const baseConfig: Record<string, any> = {};
    yKeys.forEach((key, index) => {
      const seriesConfig = config[key] || {};
      baseConfig[key] = {
        label: seriesConfig.label || key,
        color: seriesConfig.color || `hsl(var(--chart-${(index % 5) + 1}))`,
      };
    });
    return baseConfig;
  }, [yKeys, config]);

  if (loading) {
    return (
      <div
        className={cn("flex items-center justify-center", className)}
        style={{ height, width }}
      >
        <div className="text-muted-foreground text-sm">Carregando...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={cn("flex items-center justify-center", className)}
        style={{ height, width }}
      >
        <div className="text-destructive text-sm text-center">
          Erro: {error}
        </div>
      </div>
    );
  }

  // Determinar tipo de linha baseado na variante
  const getLineType = () => {
    if (variant === "linear") return "linear";
    if (variant === "step") return "step";
    return smooth ? "monotone" : "linear";
  };

  // Determinar se deve mostrar dots
  const shouldShowDots =
    showDots || variant.includes("dots") || variant.includes("labels");

  return (
    <div className={cn("w-full", className)}>
      <ChartContainer
        config={chartConfig}
        className="w-full"
        style={{ height, width }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <RechartsLineChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            {showGrid && (
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
            )}

            {showXAxis && (
              <XAxis
                dataKey={xKey}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={xAxisFormatter}
              />
            )}

            {showYAxis && (
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                domain={[0, "auto"]}
                tickFormatter={yAxisFormatter}
              />
            )}

            {showTooltip && (
              <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
            )}

            {yKeys.map((key, index) => {
              const seriesConfig = config[key] || {};
              const color =
                seriesConfig.color || `hsl(var(--chart-${(index % 5) + 1}))`;
              const lineStrokeWidth = seriesConfig.strokeWidth || strokeWidth;

              return (
                <Line
                  key={key}
                  type={getLineType()}
                  dataKey={key}
                  stroke={color}
                  strokeWidth={lineStrokeWidth}
                  dot={
                    shouldShowDots
                      ? {
                          fill: dotColor || color,
                          strokeWidth: 2,
                          r: dotSize,
                          ...(customDot && { content: customDot }),
                        }
                      : false
                  }
                  activeDot={{
                    r: dotSize + 2,
                    stroke: color,
                    strokeWidth: 2,
                    fill: "white",
                  }}
                >
                  {showLabels && (
                    <LabelList
                      dataKey={key}
                      position={labelPosition}
                      formatter={customLabel || ((value: any) => value)}
                      className="text-xs fill-muted-foreground"
                    />
                  )}
                </Line>
              );
            })}
          </RechartsLineChart>
        </ResponsiveContainer>
      </ChartContainer>

      {showLegend && (
        <div className="mt-4">
          <ChartLegend content={<ChartLegendContent />} />
        </div>
      )}
    </div>
  );
}
